import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LeadSearchInput from '../leads/LeadSearchInput'; // Assuming we need this

const EditTaskModal = ({ isOpen, onClose, taskToEdit, users, onTaskUpdated }) => {
  const [editTask, setEditTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize form state when taskToEdit changes
  useEffect(() => {
    if (taskToEdit) {
      // Prepare task data for the form (e.g., formatting dates/times)
      const taskWithTime = { ...taskToEdit };
      
      // Add debugging to see what we received
      console.log('Task to edit received:', JSON.stringify(taskToEdit, null, 2));
      
      if (taskToEdit.due_at) {
        const dueDateTime = new Date(taskToEdit.due_at);
        const year = dueDateTime.getFullYear();
        const month = (dueDateTime.getMonth() + 1).toString().padStart(2, '0');
        const day = dueDateTime.getDate().toString().padStart(2, '0');
        taskWithTime.due_date = `${year}-${month}-${day}`;
        const hours = dueDateTime.getHours().toString().padStart(2, '0');
        const minutes = dueDateTime.getMinutes().toString().padStart(2, '0');
        taskWithTime.due_time = `${hours}:${minutes}`;
      } else {
        // Set default due date to today if none provided
        const today = new Date();
        const year = today.getFullYear();
        const month = (today.getMonth() + 1).toString().padStart(2, '0');
        const day = today.getDate().toString().padStart(2, '0');
        taskWithTime.due_date = `${year}-${month}-${day}`;
        taskWithTime.due_time = '';
      }
      
      // CRITICAL: Parse the assigned_to properly
      // If it's an object with an id property, use that id
      // If it's a string that looks like a number, use that
      // Otherwise, default to empty string (null)
      if (typeof taskToEdit.assigned_to === 'object' && taskToEdit.assigned_to?.id) {
        // If assigned_to is an object with an id property
        taskWithTime.assigned_to = taskToEdit.assigned_to.id;
        console.log('Assigned to from object:', taskWithTime.assigned_to);
      } else if (typeof taskToEdit.assigned_to === 'number') {
        // If assigned_to is already a number, use as is
        taskWithTime.assigned_to = taskToEdit.assigned_to;
        console.log('Assigned to is already a number:', taskWithTime.assigned_to);
      } else if (taskToEdit.assigned_to && !isNaN(Number(taskToEdit.assigned_to))) {
        // If assigned_to is a string that can be parsed as a number
        taskWithTime.assigned_to = Number(taskToEdit.assigned_to);
        console.log('Assigned to parsed from string:', taskWithTime.assigned_to);
      } else {
        // Default to empty string
        taskWithTime.assigned_to = '';
        console.log('No valid assigned_to found, using empty string');
      }
      
      // Store the original user's name for display purposes if available
      taskWithTime.assigned_to_name = taskToEdit.assigned_to_name || '';
      
      setEditTask(taskWithTime);
    } else {
      setEditTask(null); // Reset if no task is provided
    }
  }, [taskToEdit]);

  // Handle ESC key
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        onClose(false); // Indicate no update occurred
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  const handleEditTaskChange = (e) => {
    const { name, value } = e.target;
    setEditTask(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitEditTask = async (e) => {
    e.preventDefault();
    if (!editTask) return;

    // Client-side validation before request
    if (!editTask.title) {
      setError('Title is required');
      return;
    }
    
    if (!editTask.due_date) {
      setError('Due date is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      // Prepare data for API (combine date and time if needed)
      const payload = { ...editTask };
      
      // IMPORTANT: Include the raw due_date in the payload
      // The backend expects to receive this field
      if (!payload.due_date) {
        setError('Due date is required');
        setLoading(false);
        return;
      }
      
      // Combine date and time into due_at if both are present
      if (payload.due_date && payload.due_time) {
        payload.due_at = `${payload.due_date}T${payload.due_time}:00`;
      } else if (payload.due_date) {
        // If only date is provided, default time to 00:00:00
        payload.due_at = `${payload.due_date}T00:00:00`;
      } else {
        // This should never happen due to client-side validation above
        setError('Due date is required');
        setLoading(false);
        return;
      }
      
      // DO NOT remove due_date from payload - backend validation requires it
      delete payload.due_time;

      // Handle assigned_to properly to ensure it's sent as a number or null
      if (payload.assigned_to === '' || payload.assigned_to === null || payload.assigned_to === undefined) {
        payload.assigned_to = null;
      } else if (typeof payload.assigned_to === 'string' && !isNaN(Number(payload.assigned_to))) {
        // Convert string ID to number
        payload.assigned_to = Number(payload.assigned_to);
      }
      
      // Remove the display-only assigned_to_name field to avoid confusion
      delete payload.assigned_to_name;

      // For debugging - make sure due_date is properly handled
      console.log('Task update payload:', JSON.stringify(payload, null, 2));
      console.log('Original editTask:', JSON.stringify(editTask, null, 2));

      // Ensure due_date is included in payload (backend requires it)
      payload.due_date = editTask.due_date;
      
      const res = await axios.put(
        `/api/tasks/${editTask.id}`,
        payload, // Use the prepared payload
        config
      );

      setLoading(false);
      onClose(true); // Indicate success
      if (onTaskUpdated) {
        onTaskUpdated(res.data); // Pass updated task data back if handler exists
      }
    } catch (err) {
      console.error('Error updating task:', err);
      setError(err.response?.data?.message || 'Failed to update task. Please try again.');
      setLoading(false);
    }
  };

  // Return null if not open or no task data
  if (!isOpen || !editTask) return null;

  // --- Modal JSX will go here in the next step ---
  // Placeholder for now
  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => onClose(false)}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        {/* Modal panel */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* --- FORM JSX based on TasksList.js modal --- */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Edit Task</h3>
                {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
                <form onSubmit={handleSubmitEditTask}>
                  {/* Title */}
                  <div className="mb-4">
                    <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text" name="title" id="edit-title" required
                      value={editTask.title || ''} onChange={handleEditTaskChange}
                      className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                    />
                  </div>
                  {/* Description */}
                  <div className="mb-4">
                    <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      name="description" id="edit-description" rows="3"
                      value={editTask.description || ''} onChange={handleEditTaskChange}
                      className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                    ></textarea>
                  </div>
                  {/* Due Date & Time / Priority */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="space-y-2">
                      <label htmlFor="edit-due_date" className="block text-sm font-medium text-gray-700 mb-1">Due Date & Time</label>
                      <div className="flex space-x-2">
                        <input
                          type="date" name="due_date" id="edit-due_date" required
                          value={editTask.due_date || ''} onChange={handleEditTaskChange}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                        />
                        <input
                          type="time" name="due_time" id="edit-due_time"
                          value={editTask.due_time || ''} onChange={handleEditTaskChange}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="edit-priority" className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      <select
                        name="priority" id="edit-priority" value={editTask.priority || 'medium'} onChange={handleEditTaskChange}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>
                  {/* Assign To / Related Lead */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="edit-assigned_to" className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                      <select
                        name="assigned_to" id="edit-assigned_to" value={editTask.assigned_to || ''} onChange={handleEditTaskChange}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                      >
                        <option value="">Select a user</option>
                        {(users || []).map(user => (
                          <option key={user.id} value={user.id}>{user.name}</option>
                        ))}
                      </select>
                      {/* Show the current assignee for reference */}
                      {editTask.assigned_to_name && (
                        <div className="mt-1 text-xs text-gray-500">
                          Currently assigned to: {editTask.assigned_to_name}
                        </div>
                      )}
                    </div>
                    <div>
                      <label htmlFor="edit-lead_id" className="block text-sm font-medium text-gray-700 mb-1">Related Lead</label>
                      <LeadSearchInput
                        value={editTask.lead_id || ''}
                        onChange={(leadId) => handleEditTaskChange({ target: { name: 'lead_id', value: leadId } })}
                        placeholder="Search leads by name or email"
                      />
                    </div>
                  </div>
                  {/* Status / Type */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="edit-status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        name="status" id="edit-status" value={editTask.status || 'pending'} onChange={handleEditTaskChange}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                      >
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="edit-type" className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select
                        name="type" id="edit-type" value={editTask.type || 'task'} onChange={handleEditTaskChange}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
                      >
                        <option value="task">Sales Task</option>
                        <option value="support">Support Ticket</option>
                      </select>
                    </div>
                  </div>
                  {/* Buttons */}
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit" disabled={loading}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {loading ? 'Updating...' : 'Update Task'}
                    </button>
                    <button
                      type="button" onClick={() => onClose(false)}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          {/* --- END OF FORM JSX --- */}
        </div>
      </div>
    </div>
  );
};

export default EditTaskModal;
